import {
  Box,
  CalendarTodayOutlinedIcon,
  Divider,
  Flex,
  LocalOfferOutlinedIcon,
  mergeSx,
  Text,
  Card,
  type CardProps,
  LabelColor,
} from '@shared/components';
import { formatDate } from '@shared/utils';

export type QuestionReviewReviewerCardProps = Pick<CardProps, 'sx'> & {
  order: number;
  name: string;
  taskCode: string;
  color?:
    | 'primary'
    | 'secondary'
    | 'info'
    | 'warning'
    | 'success'
    | 'error'
    | 'grey'
    | 'deepPurple';
  dueDate: Date | null;
  children?: React.ReactNode;
  endAdornment?: React.ReactNode;
  infoEndAdornment?: React.ReactNode;
};

export default function QuestionReviewReviewerCard(
  props: Readonly<QuestionReviewReviewerCardProps>,
) {
  const {
    order,
    name,
    taskCode,
    color = 'grey',
    dueDate,
    children,
    sx,
    endAdornment,
    infoEndAdornment,
  } = props;

  return (
    <Card
      elevation={0}
      sx={mergeSx(
        (theme) => ({
          borderRadius: theme.shape.rounded(2),
          border: `1px solid ${theme.palette.grey[300]}`,
        }),
        sx,
      )}
    >
      <Flex
        sx={(theme) => ({
          alignItems: 'center',
          justifyContent: 'space-between',
          p: theme.spacing(3, 4),
          backgroundColor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          height: '60px',
        })}
      >
        <Text variant='body1Semibold' color='text.secondary'>
          ผู้ทบทวนข้อสอบ {order}
        </Text>
        {endAdornment ? (
          <Flex
            sx={(theme) => ({
              alignItems: 'center',
              gap: theme.spacing(2),
            })}
          >
            {endAdornment}
          </Flex>
        ) : null}
      </Flex>
      <Box
        sx={(theme) => ({
          p: theme.spacing(6),
        })}
      >
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(4),
            paddingLeft: theme.spacing(4),
            borderLeft: `4px solid ${color !== 'grey' ? theme.palette[color].main : LabelColor.GREY}`,
          })}
        >
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(2),
            })}
          >
            <Flex
              sx={(theme) => ({
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: theme.spacing(2),
              })}
            >
              <Text variant='body1Semibold'>{name}</Text>
              {infoEndAdornment ? <Box>{infoEndAdornment}</Box> : null}
            </Flex>
            <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
              <Flex
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  alignItems: 'center',
                  gap: theme.spacing(1),
                })}
              >
                <LocalOfferOutlinedIcon sx={{ fontSize: '16px' }} />
                <Text variant='body2'>Task ID:</Text>
                <Text variant='body2'>{taskCode}</Text>
              </Flex>
              <Flex
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  alignItems: 'center',
                  gap: theme.spacing(1),
                })}
              >
                <CalendarTodayOutlinedIcon sx={{ fontSize: '16px' }} />
                <Text variant='body2'>Due Date:</Text>
                <Text variant='body2'>{formatDate(dueDate)}</Text>
              </Flex>
            </Flex>
          </Flex>
          <Divider />
          {children}
        </Flex>
      </Box>
    </Card>
  );
}
